import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Stack,
  Typography,
  Snackbar,
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import { motion } from 'framer-motion';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import ModernLayout from '../components/ModernLayout';
import { ABAS } from '../telas/abas';
import { contagemDeSinais } from '../telas/apresentacao';
import { CAMPOS_DO_BALANCO_CURTO, CATALOGO_DE_INDICADORES } from '../diagnostico/catalogo';
import ProximoPasso from '../telas/componentes/ProximoPasso';
import EsqueletoDaTela from '../telas/componentes/EsqueletoDaTela';
import TelaSaude from '../telas/TelaSaude';
import TelaResultado from '../telas/TelaResultado';
import TelaCaixaCiclo from '../telas/TelaCaixaCiclo';
import TelaDividaRetorno from '../telas/TelaDividaRetorno';
import TelaAnalise from '../telas/TelaAnalise';
import TelaPainel from '../telas/TelaPainel';
import TelaDados from '../telas/TelaDados';
import TelaPlanos from '../telas/TelaPlanos';
import DesdeAUltimaVisita from '../telas/componentes/DesdeAUltimaVisita';
import { mereceAPrimeiraDobra } from '../telas/planoDeAcaoApresentacao';
import { marcarTarefa } from '../services/planoDeAcaoService';
import { lerDiagnostico } from '../diagnostico/leitura';
import FecharMes from '../components/FecharMes/FecharMes';
import BaixarModelo from '../components/BaixarModelo';
import ImportarPlanilhaLivre from '../components/ImportarPlanilhaLivre';
import UploadProgress from '../components/UploadProgress';
import DRETab from '../components/DRETab';
import TelaBalanco from '../telas/TelaBalanco';
import AlertConfigTab from '../components/AlertConfigTab';
import InsightsConfigEditor from '../components/InsightsConfigEditor';
import WhatIfPanel from '../components/WhatIfPanel';
import AuthDialog from '../components/AuthDialog';
import Wizard from '../components/ManualEntry/Wizard';
import API_URL, { API_ENDPOINTS } from '../config/api';
import useLocalStorage from '../hooks/useLocalStorage';
import authService from '../services/authService';
import { construirDRE, construirBalanco } from '../services/manualEntryTransform';
import { ultimoValor, ultimoIndiceComDado, formatarMoeda, formatarDecimal } from '../utils/series';
import { rotuloDoMes } from '../utils/periodos';
import BlocoBloqueado from '../telas/componentes/BlocoBloqueado';
import { projecaoDoDashboard } from '../services/periodoService';
import { gerarParecer } from '../services/parecerService';
import { listarPlanos } from '../services/planoService';
import { ofertaAoConcluirLancamento } from '../telas/planosApresentacao';
import OfertaAoConcluir from '../telas/componentes/OfertaAoConcluir';
import { LARGURA_DE_LEITURA } from '../telas/layout';
import {
  CHAVES_DADOS_LOCAIS,
  CHAVE_DONO_CACHE,
  CHAVE_DRE,
  CHAVE_BALANCO,
  CHAVE_DASHBOARD,
  CHAVE_ARQUIVOS,
  CHAVE_ULTIMO_SALVAMENTO,
  descartarRascunhoDoWizard
} from '../config/chavesLocais';

const SNAPSHOT_VERSION = 1;

const computeSnapshotChecksum = async (snapshot) => {
  const json = JSON.stringify(snapshot);
  if (window.crypto && window.crypto.subtle && typeof TextEncoder !== 'undefined') {
    const buffer = new TextEncoder().encode(json);
    const digest = await window.crypto.subtle.digest('SHA-256', buffer);
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  }
  // Fallback simples quando WebCrypto não está disponível
  let hash = 0;
  for (let i = 0; i < json.length; i += 1) {
    const chr = json.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0;
  }
  return 'fallback-' + Math.abs(hash);
};

const limparCacheLocal = () => {
  try {
    CHAVES_DADOS_LOCAIS.forEach((chave) => window.localStorage.removeItem(chave));
    window.localStorage.removeItem(CHAVE_DONO_CACHE);
  } catch (error) {
    console.error('Falha ao limpar cache local:', error);
  }
};

/** As telas que leem o diagnóstico — as que ganham guia e esqueleto. */
const TELAS_DE_LEITURA = [
  ABAS.SAUDE, ABAS.RESULTADO, ABAS.CAIXA_CICLO, ABAS.DIVIDA_RETORNO, ABAS.PAINEL, ABAS.ANALISE
];

const ModernDashboard = () => {
  // Persistir dados no localStorage para não perder ao recarregar página
  const [dreData, setDreData] = useLocalStorage(CHAVE_DRE, null);
  const [balancoData, setBalancoData] = useLocalStorage(CHAVE_BALANCO, null);
  const [dashboardData, setDashboardData] = useLocalStorage(CHAVE_DASHBOARD, null);
  const [uploadedFiles, setUploadedFiles] = useLocalStorage(CHAVE_ARQUIVOS, {
    dre: false,
    balanco: false,
  });
  const [lastSavedInfo, setLastSavedInfo] = useLocalStorage(CHAVE_ULTIMO_SALVAMENTO, null);

  // Estados que NÃO precisam persistir
  const [activeTab, setActiveTab] = useState(ABAS.DADOS);

  // DEEP-LINK DA LANDING.
  //
  // A landing manda o visitante para `/app?tela=planos` quando ele clica em
  // "Quero o acompanhamento", e para `/app?entrar=1` quando clica em "Entrar".
  // Sem isto os dois caíam na tela de Dados e o visitante perdia exatamente o
  // que tinha pedido. Só ids de tela conhecidos são aceitos; qualquer outro
  // valor é ignorado em silêncio.
  useEffect(() => {
    const parametros = new URLSearchParams(window.location.search);
    const tela = parametros.get('tela');
    const querEntrar = parametros.get('entrar') === '1';

    if (tela && Object.values(ABAS).includes(tela)) setActiveTab(tela);
    if (querEntrar) setAuthDialogOpen(true);

    // A URL volta a ser `/app`: recarregar a página não deve reabrir o diálogo
    // nem prender o usuário na tela que ele já saiu.
    if (tela || querEntrar) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);
  /**
   * A tela em que o usuário está AGORA, legível de dentro de uma função async.
   *
   * Uma requisição que começa na tela A e responde três segundos depois decidia
   * a navegação com a tela A — mesmo que a pessoa já estivesse na B. Atribuído
   * durante o render de propósito: em `useEffect` ele ficaria um passo atrás
   * justamente no instante em que a resposta chega.
   */
  const telaAtual = useRef(activeTab);
  telaAtual.current = activeTab;
  const [loading, setLoading] = useState(false);
  const [entryMode, setEntryMode] = useState('selection');
  const [scenarioData, setScenarioData] = useState(null);
const [scenarioLoading, setScenarioLoading] = useState(false);
const [exporting, setExporting] = useState(false);
const [authState, setAuthState] = useState({ status: 'checking', user: null });
const [authDialogOpen, setAuthDialogOpen] = useState(false);
const isAdmin = authState.user?.role === 'admin';
const [saveLoading, setSaveLoading] = useState(false);
  const [feedback, setFeedback] = useState({ open: false, message: '', severity: 'success' });
  const currentLastSavedAt = authState.user && lastSavedInfo?.by === authState.user.email ? lastSavedInfo.savedAt : null;
  const defaultAdjustments = {
    faturamentoPercent: 0,
    custosVariaveisPercent: 0,
    custosFixosPercent: 0,
    despesasFinanceirasPercent: 0
  };
  const [whatIfAdjustments, setWhatIfAdjustments] = useState(defaultAdjustments);
  // Erro da simulação com nome e recuperação, exibido DENTRO do painel. Ele saía
  // por `alert()` do navegador: sem indicação do que fazer e sem rastro depois
  // de fechado, num produto que tem aviso próprio em todo o resto.
  const [scenarioError, setScenarioError] = useState(null);
  // Apagar tudo pede confirmação de verdade, não a caixa cinza do sistema
  // operacional — que não diz o que será apagado nem combina com a tela.
  const [confirmarLimpeza, setConfirmarLimpeza] = useState(false);

  // De onde a pessoa chegou na tela de planos, e o que ficou de fora quando o
  // limite do plano barrou meses. "Assine" sem motivo é anúncio; com motivo é
  // resposta a uma pergunta que ela acabou de fazer.
  const [contextoDePlanos, setContextoDePlanos] = useState({ origem: null, bloqueio: null });
  /**
   * O parecer tem estado PRÓPRIO de trabalho, separado do `loading` do
   * dashboard.
   *
   * Ele deixou de ser um efeito colateral da geração do dashboard: agora tem
   * rota própria (`POST /api/insights/parecer`), que é a única do produto que
   * aciona modelo por pedido do usuário. Reaproveitar o `loading` geral faria a
   * tela inteira entrar em carregamento por causa de um bloco só.
   */
  const [gerandoParecer, setGerandoParecer] = useState(false);

  /**
   * Qual iniciativa está sendo gravada agora.
   *
   * Guarda a CHAVE, e não um booleano: com um booleano, marcar uma tarefa
   * desabilitaria os controles de todas as outras, e o plano inteiro congelaria
   * a cada clique.
   */
  const [salvandoTarefa, setSalvandoTarefa] = useState(null);
  /**
   * A oferta do fim do lançamento. Transitória de propósito: ela pertence ao
   * instante em que o valor acabou de ser entregue, não à tela — banner
   * permanente numa tela que a pessoa visita todo mês azeda rápido.
   */
  const [ofertaDeAssinatura, setOfertaDeAssinatura] = useState(null);
  // Aviso que NÃO some sozinho: ele conta que meses não foram gravados, e
  // mensagem de perda de dado não pode depender de a pessoa estar olhando.
  const [avisoDePeriodos, setAvisoDePeriodos] = useState(null);

  // A leitura única do payload. Sete telas resolvendo por conta própria onde
  // mora cada número são sete lugares para divergir — foi assim que este
  // produto acabou com quatro pares de indicadores exibindo o mesmo valor com
  // nomes diferentes.
  const leitura = useMemo(() => lerDiagnostico(dashboardData), [dashboardData]);

  /**
   * O resumo da visita, montado pelo servidor junto da análise.
   *
   * Só existe para quem tem conta e mês gravado: visitante anônimo não tem onde
   * guardar visita nem tarefa, e recebe o plano de ação como leitura — inteiro,
   * porque ele é do tier gratuito.
   */
  const retornoDaVisita = leitura?.insights?.retorno || null;
  const temPrimeiraDobraDeRetorno = mereceAPrimeiraDobra(retornoDaVisita);

  /**
   * O simulador depende do balanço tanto quanto os blocos vizinhos do Painel.
   *
   * A rota `/api/insights/simulate` RECUSA a chamada sem balanço — é ele que dá
   * a estrutura sobre a qual a cobertura da dívida e o runway são medidos. Antes
   * o painel aparecia destravado ao lado de três blocos com cadeado e o clique
   * respondia com um alerta do sistema operacional mandando "gerar o dashboard
   * completo", que já estava gerado, por quem tinha usado o caminho de primeira
   * classe "Pular esta etapa". Agora ele usa o MESMO bloco fechado dos vizinhos.
   *
   * A condição olha `dashboardData.balanco`, que é exatamente o que a rota exige
   * — e não uma seção do catálogo: as contas de dívida NÃO exigem balanço no
   * catálogo (`exigeBalanco: false`), então uma seção como portão deixaria o
   * painel destravado justamente para quem pulou o balanço.
   */
  const temBalanco = Boolean(dashboardData?.balanco);
  const estadoDoSimulador = useMemo(() => ({
    campos: CAMPOS_DO_BALANCO_CURTO,
    // Os nomes vêm do catálogo, nunca da mão: bloco fechado que promete um
    // indicador com nome diferente do que aparece depois é promessa quebrada.
    bloqueados: CATALOGO_DE_INDICADORES
      .filter((indicador) => ['runwayMeses', 'dscr'].includes(indicador.chave))
      .map((indicador) => indicador.nome),
    chamada: `${CAMPOS_DO_BALANCO_CURTO.length} campos do balanço destravam a simulação de cenário`
  }), []);

  // Exportar exige a análise gerada, não apenas "ter dado". O botão do cabeçalho
  // ficava habilitado e o erro só aparecia depois do clique — convite ao engano.
  const podeExportar = Boolean(dashboardData?.consultantInsights);

  // O que a tela de planos manda para o teaser. MEMOIZADO de propósito: um
  // objeto novo a cada render dispararia uma requisição por render — e mesmo
  // sendo determinístico do outro lado, isso é um laço infinito.
  const dadosParaTeaser = useMemo(
    () =>
      dashboardData
        ? {
          dre: dashboardData.dre,
          balanco: dashboardData.balanco,
          adicionais: dashboardData.indicadoresAdicionais
        }
        : null,
    [dashboardData]
  );

  // Inicializar activeTab baseado na existência de dados salvos (executar apenas uma vez)
  useEffect(() => {
    if (dashboardData) {
      setActiveTab(ABAS.SAUDE);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { authenticated, user } = await authService.getSession();
        if (authenticated) {
          descartarCacheDeOutroUsuario(user);
          setAuthState({ status: 'authenticated', user });
        } else {
          setAuthState({ status: 'anonymous', user: null });
        }
      } catch (error) {
        setAuthState({ status: 'anonymous', user: null });
      }
    };
    checkSession();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Usuário logado: o histórico da conta é lido do servidor a cada carregamento.
  // É o que faz os dados sobreviverem a outro dia/dispositivo — e o que faz duas
  // abas do mesmo produto pararem de discordar. O cache do navegador serve para
  // a tela aparecer sem espera, não para ser a verdade: quem fechou março no
  // celular e recarregou o desktop via o desktop insistir no que ele tinha
  // guardado de manhã.
  useEffect(() => {
    if (authState.status !== 'authenticated') return undefined;

    let cancelado = false;
    // As duas recebem uma FUNÇÃO, não o booleano: passar o valor congela `false`
    // no instante da chamada, e o `await` no meio existe justamente para que a
    // resposta chegue depois. Logout durante o fetch gravaria o dado do usuário
    // anterior depois da limpeza de cache.
    const foiCancelado = () => cancelado;
    // Já tem número na tela? Então a conferência é silenciosa: ela existe para
    // atualizar o que está lá, não para trocar por barras cinzas o que a pessoa
    // está lendo — nem para arrastá-la de volta para a Saúde.
    const jaTemDado = Boolean(dashboardData);

    (async () => {
      const veioDaSerie = await carregarSerieDeCompetencias(foiCancelado, {
        irParaSaude: !jaTemDado,
        silencioso: jaTemDado
      });
      // O snapshot é mais velho que a série e só entra quando não há nada: ele
      // não pode passar por cima do que o navegador já mostra.
      if (veioDaSerie || cancelado || jaTemDado) return;
      await carregarUltimoSnapshot(foiCancelado);
    })();

    return () => {
      cancelado = true;
    };
  }, [authState.status]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * A série por competência é a fonte preferida do dashboard.
   *
   * O snapshot guarda a foto de uma importação; a série guarda o histórico, e é
   * ela que contém o mês que o usuário fechou depois. Sem esta leitura, fechar
   * abril não faz abril aparecer em lugar nenhum.
   */
  const carregarSerieDeCompetencias = async (foiCancelado = () => false, opcoes = {}) => {
    try {
      const projecoes = await projecaoDoDashboard();
      if (foiCancelado() || !projecoes) return false;

      const { paraExibir, paraCalcular } = projecoes;
      const ultimoBalanco = paraExibir.balancos?.[paraExibir.balancos.length - 1] || null;
      const balanco = ultimoBalanco?.retrato || null;

      setDreData(paraExibir);
      setBalancoData(balanco);
      setUploadedFiles({ dre: true, balanco: Boolean(balanco) });
      await generateCompleteDashboard(paraCalcular, balanco, {
        dreParaExibir: paraExibir,
        // A data do balanço viaja separada do retrato: o retrato é enviado ao
        // backend e não pode ganhar campo nosso, mas a tela precisa dizer de
        // que data são os saldos.
        referenciaDoBalanco: ultimoBalanco
          ? { competencia: ultimoBalanco.competencia, dataRef: ultimoBalanco.dataRef }
          : null,
        // Ler a série NÃO é produzir dado novo: quem manda gravar é quem
        // preencheu ou corrigiu, e nunca a releitura do que já está gravado.
        ...opcoes
      });
      return true;
    } catch (error) {
      // Sem série gravada ainda (empresa recém-criada) é o caminho normal, não
      // um erro: cai para o snapshot.
      return false;
    }
  };

  const carregarUltimoSnapshot = async (foiCancelado) => {
    try {
      const resposta = await fetch(`${API_URL}/api/upload/latest`, { credentials: 'include' });
      if (!resposta.ok) return;

      const corpo = await resposta.json();
      const snapshot = corpo?.data;
      if (foiCancelado() || !snapshot || (!snapshot.dre && !snapshot.balanco)) return;

      setDreData(snapshot.dre);
      setBalancoData(snapshot.balanco);
      setUploadedFiles({ dre: Boolean(snapshot.dre), balanco: Boolean(snapshot.balanco) });
      await generateCompleteDashboard(snapshot.dre, snapshot.balanco, { irParaSaude: true });
      showFeedback('Recuperamos o seu último lançamento salvo.', 'info');
    } catch (error) {
      console.error('Falha ao recuperar último lançamento:', error);
    }
  };

  /** Depois de fechar um mês, a série tem um mês a mais: recarrega. */
  /**
   * Fechou ou corrigiu um mês: a série é relida ANTES de qualquer outra coisa.
   *
   * A releitura não é cosmética. O salvamento automático do snapshot regrava as
   * competências a partir do que está no navegador; se a memória daqui ficar
   * velha depois de uma correção, o salvamento seguinte grava o número antigo
   * por cima do corrigido — e número que muda em silêncio é o pior modo de
   * falha deste produto.
   */
  const handleMesFechado = async (resultado) => {
    const recarregou = await carregarSerieDeCompetencias();
    if (!recarregou) return;
    setActiveTab(ABAS.SAUDE);
    // `revisao` só vem da correção; `gravados`, de fechar um mês novo.
    const corrigiu = Boolean(resultado?.revisao);
    // A oferta vem DEPOIS da releitura, e leva o mês: ela só faz sentido com o
    // resultado novo já na tela, e nomear o mês é o que a separa de um anúncio.
    oferecerAssinatura({
      competencia: resultado?.competencia || resultado?.gravados?.[0]?.competencia || null,
      corrigiu
    });
    showFeedback(
      corrigiu
        ? 'Mês corrigido. A versão anterior continua guardada no histórico.'
        : 'Mês fechado. O histórico foi atualizado.',
      'success'
    );
  };

  /**
   * Monta a oferta de assinatura para quem acabou de concluir um lançamento.
   *
   * O catálogo e o plano vêm da MESMA rota que a tela de planos usa
   * (`GET /api/planos`, pública e sem modelo nenhum): escrever aqui uma segunda
   * lista do que cada plano entrega criaria duas versões da promessa.
   *
   * Falha de rede aqui não vira erro na tela. A oferta é acessória; quem acabou
   * de fechar um mês quer ver o resultado, e um alerta vermelho no lugar disso
   * seria o produto reclamando de algo que não interessa a ele.
   */
  const oferecerAssinatura = async ({ competencia = null, corrigiu = false } = {}) => {
    try {
      const catalogo = await listarPlanos();
      setOfertaDeAssinatura(ofertaAoConcluirLancamento({
        planoAtual: catalogo?.atual,
        catalogo: catalogo?.planos,
        competencia,
        corrigiu
      }));
    } catch (error) {
      setOfertaDeAssinatura(null);
    }
  };

  /**
   * Pedir — ou atualizar — o parecer por IA.
   *
   * Antes isto era `generateCompleteDashboard`, ou seja, a MESMA rota que a tela
   * chama a cada carregamento de página. Duas consequências: o assinante gastava
   * uma chamada de modelo por recarregamento, e o parecer nunca podia envelhecer
   * — ele se refazia sozinho antes de alguém notar que os números tinham mudado.
   *
   * Agora a rota é própria e o resultado é GRAVADO com a impressão digital do
   * dado que o produziu. Só a análise é substituída na tela: o DRE, o balanço e
   * a janela de exibição não vieram desta requisição e não podem ser trocados
   * por ela.
   */
  const pedirParecer = async () => {
    if (gerandoParecer) return;
    setGerandoParecer(true);
    try {
      const { consultantInsights } = await gerarParecer();
      if (!consultantInsights) throw new Error('O parecer voltou vazio.');
      setDashboardData((atual) => (atual ? { ...atual, consultantInsights } : atual));
    } catch (error) {
      // O parecer anterior CONTINUA na tela, com a data e os meses dele: uma
      // recusa do provedor não pode apagar o que o cliente já tinha lido.
      showFeedback(error.message || 'Não foi possível gerar o parecer agora.', 'error');
    } finally {
      setGerandoParecer(false);
    }
  };

  /**
   * Marcar, adiar ou reabrir uma iniciativa do plano.
   *
   * Não aciona modelo e não depende de assinatura: o plano 30-60-90 é
   * determinístico e o acompanhamento dele é do tier gratuito.
   *
   * O servidor devolve o plano INTEIRO já redecorado, e é ele que entra no
   * estado. Aplicar a mudança só no item clicado deixaria a contagem do
   * cabeçalho, a ordenação e o resumo da visita descrevendo o plano de antes —
   * três lugares mentindo sobre o mesmo gesto.
   */
  const marcarIniciativa = async (chave, estado) => {
    if (!chave || salvandoTarefa) return;
    setSalvandoTarefa(chave);
    try {
      const { plano, retorno } = await marcarTarefa(chave, estado);
      setDashboardData((atual) => (atual && atual.consultantInsights
        ? {
            ...atual,
            consultantInsights: {
              ...atual.consultantInsights,
              ...(plano ? { actionPlan: plano } : {}),
              ...(retorno ? { retorno } : {})
            }
          }
        : atual));
    } catch (error) {
      // Nada foi aplicado na tela antes da resposta, então não há o que
      // desfazer: o estado exibido continua sendo o que está gravado.
      showFeedback(error.message || 'Não foi possível atualizar a iniciativa agora.', 'error');
    } finally {
      setSalvandoTarefa(null);
    }
  };

  useEffect(() => {
    if (!isAdmin && activeTab === ABAS.CONFIG) {
      setActiveTab(ABAS.DADOS);
    }
  }, [isAdmin, activeTab]);

  const showFeedback = (message, severity = 'success') => {
    setFeedback({ open: true, message, severity });
  };

  /** Zera o que está em memória além do localStorage — o hook guarda cópia no estado. */
  const zerarDadosEmMemoria = () => {
    setDreData(null);
    setBalancoData(null);
    setDashboardData(null);
    setUploadedFiles({ dre: false, balanco: false });
    setLastSavedInfo(null);
    setScenarioData(null);
    setWhatIfAdjustments(defaultAdjustments);
    setEntryMode('selection');
    setActiveTab(ABAS.DADOS);
  };

  /**
   * Cache local pertence a um usuário. Se quem entrou agora é outro (ou o
   * cache veio de uma sessão anônima), descarta antes de exibir qualquer
   * número — senão um usuário veria o financeiro do anterior.
   */
  const descartarCacheDeOutroUsuario = (user) => {
    const donoAtual = user?.id != null ? String(user.id) : null;
    let donoSalvo = null;
    try {
      donoSalvo = window.localStorage.getItem(CHAVE_DONO_CACHE);
    } catch (error) {
      donoSalvo = null;
    }

    if (donoSalvo !== donoAtual) {
      limparCacheLocal();
      zerarDadosEmMemoria();
    }

    try {
      if (donoAtual) window.localStorage.setItem(CHAVE_DONO_CACHE, donoAtual);
    } catch (error) {
      // localStorage indisponível (aba anônima com restrição) — segue sem cache
    }
  };

  const handleLoginSuccess = (user) => {
    descartarCacheDeOutroUsuario(user);
    setAuthState({ status: 'authenticated', user });
    setAuthDialogOpen(false);
    showFeedback('Login realizado com sucesso!');
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      // Ignorar erros de logout para UX mais simples
    }
    limparCacheLocal();
    zerarDadosEmMemoria();
    setAuthState({ status: 'anonymous', user: null });
    showFeedback('Sessão encerrada.', 'info');
  };

  const handleOpenLogin = () => {
    setAuthDialogOpen(true);
  };

  const handleCloseLogin = () => {
    setAuthDialogOpen(false);
  };

  const buildSnapshot = () => {
    if (!dashboardData) return null;
    return {
      dre: dashboardData.dre || dreData,
      balanco: dashboardData.balanco || balancoData,
      indicadoresAdicionais: dashboardData.indicadoresAdicionais || null,
      alertas: dashboardData.alertas || null,
      consultantInsights: dashboardData.consultantInsights || null
    };
  };

  const handleFeedbackClose = (_event, reason) => {
    if (reason === 'clickaway') return;
    setFeedback((prev) => ({ ...prev, open: false }));
  };

  /**
   * Grava o histórico. `automatico` marca a gravação que acontece sozinha logo
   * depois de gerar o dashboard.
   *
   * POR QUE ELA EXISTE: dez etapas do questionário preenchidas, o dashboard na
   * tela, e nada persistia até o usuário reparar num botão "Salvar" no alto da
   * página. Quem fechasse a aba perdia tudo — e não havia aviso nenhum. Salvar
   * o que o usuário acabou de produzir é o comportamento óbvio; o botão fica,
   * para regravar depois de uma correção.
   */
  const salvarHistorico = async ({ automatico = false } = {}) => {
    if (authState.status !== 'authenticated') {
      if (!automatico) setAuthDialogOpen(true);
      return;
    }
    if (!dashboardData) {
      if (!automatico) showFeedback('Importe as planilhas antes de salvar o histórico.', 'warning');
      return;
    }

    if (saveLoading) return;

    try {
      setSaveLoading(true);
      const snapshot = buildSnapshot();
      const checksum = await computeSnapshotChecksum(snapshot);
      const metadata = {
        approximateSize: JSON.stringify(snapshot).length,
        uploadedAt: dashboardData?.timestamp || new Date().toISOString(),
        source: 'web-dashboard',
        month: dashboardData?.dre?.periodo?.mes || null,
        year: dashboardData?.dre?.periodo?.ano || null
      };

      const response = await fetch(API_ENDPOINTS.UPLOAD_SAVE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          payloadVersion: SNAPSHOT_VERSION,
          checksum,
          snapshot,
          metadata
        })
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message || 'Erro ao salvar histórico');
      }

      const savedAt = result.data?.savedAt || new Date().toISOString();
      setLastSavedInfo({ savedAt, checksum, by: authState.user?.email || null });
      setAuthState((prev) => {
        if (prev.status !== 'authenticated' || !prev.user) return prev;
        return {
          status: 'authenticated',
          user: {
            ...prev.user,
            savedCount: (prev.user.savedCount || 0) + 1
          }
        };
      });
      // O servidor diz quais meses NÃO entraram na série por causa do limite do
      // plano. Sem isto o usuário lia "salvo com sucesso", fechava o navegador,
      // e no dia seguinte encontrava menos meses do que tinha salvo.
      const bloqueio = result.data?.periodos || null;
      if (bloqueio?.bloqueados?.length) {
        setAvisoDePeriodos(bloqueio);
      } else {
        showFeedback(automatico ? 'Lançamento salvo no seu histórico.' : 'Histórico salvo com sucesso!');
      }
    } catch (error) {
      showFeedback(error.message || 'Erro ao salvar histórico', 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  // Dados e uma tela que se VISITA. A versao anterior zerava o dashboard ao
  // entrar aqui, o que fazia "conferir o que ja lancei" custar um
  // recarregamento inteiro.
  const navigateToImport = () => {
    setActiveTab(ABAS.DADOS);
    setEntryMode('selection');
  };

  const handleTabChange = (tabId) => {
    // Trocar de tela encerra o instante a que a oferta pertence. Sem isto ela
    // sobreviveria à navegação e viraria exatamente o banner permanente que a
    // decisão de produto proíbe.
    setOfertaDeAssinatura(null);

    if (tabId === ABAS.DADOS) {
      navigateToImport();
      return;
    }

    // Fechar o mês é a porta de entrada de quem já tem histórico: exigir
    // dashboard carregado antes trancaria justamente o uso recorrente.
    if (tabId === ABAS.FECHAR_MES) {
      setActiveTab(tabId);
      return;
    }

    // Planos também não exige dashboard: quem tropeça no limite pode nem ter
    // conseguido gerar um. Chegando pelo MENU, a tela não tem contexto — o
    // motivo de visita anterior não pode sobreviver a uma navegação livre.
    if (tabId === ABAS.PLANOS) {
      setContextoDePlanos({ origem: null, bloqueio: null });
      setActiveTab(tabId);
      return;
    }

    if (!dashboardData) {
      return;
    }

    setActiveTab(tabId);
  };

  /**
   * Ir para os planos SABENDO por quê.
   *
   * Os CTAs de paywall passam por aqui, e não por `handleTabChange`, porque o
   * motivo da visita é o que separa uma resposta de um anúncio.
   */
  const irParaPlanos = (origem, bloqueio = null) => {
    setContextoDePlanos({ origem: origem || null, bloqueio });
    setActiveTab(ABAS.PLANOS);
  };

  // "O mais recente" é o último período COM medição, e não a última posição do
  // vetor: com N períodos a janela pode terminar num mês ainda não lançado.
  const getLatestValue = (value) => {
    if (Array.isArray(value)) return ultimoValor(value);
    if (value && typeof value === 'object' && Array.isArray(value.valores)) {
      return ultimoValor(value.valores);
    }
    if (typeof value === 'number') return value;
    return null;
  };

  const getAdditionalMetric = (dataset, key) => {
    if (!dataset) return null;
    return getLatestValue(dataset[key]);
  };

  const baseAdditional = dashboardData?.indicadoresAdicionais || {};
  const scenarioAdditional = scenarioData?.indicadoresAdicionais || {};

  const baseSummary = dashboardData?.consultantInsights?.summary;
  const scenarioSummary = scenarioData?.consultantInsights?.summary;

  /**
   * O mês que a projeção compara — e que dá nome ao rótulo.
   *
   * O painel escrevia "DSCR (mês 3)" fixo enquanto o valor vinha do último mês
   * COM medição. Para quem fecha o mês há um ano, a tela dizia mês 3 e mostrava
   * o mês 12. É a mesma classe de defeito que já custou uma correção nos
   * gráficos, e ela não podia voltar pela porta do simulador.
   */
  const serieDeLucro = dashboardData?.dre?.indicadores?.lucro?.real?.valores || null;
  const indiceProjetado = ultimoIndiceComDado(serieDeLucro);
  const competenciaProjetada = indiceProjetado >= 0
    ? rotuloDoMes(dashboardData?.dre, indiceProjetado, 'longo')
    : null;

  const valorNoIndice = (serie) =>
    (Array.isArray(serie) && indiceProjetado >= 0 ? serie[indiceProjetado] ?? null : null);

  /**
   * Os destaques do cenário. Liquidez e caixa sobre dívidas de curto prazo SAÍRAM
   * daqui: eles vêm do balanço, que a simulação não ajusta, então exibiam
   * "1,47 → 1,47" com a seta de "não mudou" — uma resposta falsa a uma pergunta
   * que o painel nunca poderia responder. O que entrou no lugar é o que de fato
   * reage: o resultado do mês e o ponto de equilíbrio.
   *
   * Nenhum valor vira zero na falta: ausência viaja como `null` e o painel
   * mostra travessão.
   */
  const scenarioHighlights = scenarioData
    ? [
        {
          key: 'lucro_real',
          label: 'Resultado do mês',
          unidade: 'lucro real, em reais',
          current: valorNoIndice(serieDeLucro),
          scenario: valorNoIndice(scenarioData?.dre?.indicadores?.lucro?.real?.valores),
          formatar: formatarMoeda
        },
        {
          key: 'ponto_equilibrio',
          label: 'Ponto de equilíbrio',
          unidade: 'faturamento necessário no mês',
          current: valorNoIndice(dashboardData?.dre?.indicadores?.pontoEquilibrio?.valores),
          scenario: valorNoIndice(scenarioData?.dre?.indicadores?.pontoEquilibrio?.valores),
          formatar: formatarMoeda
        },
        {
          key: 'score',
          label: 'Score financeiro',
          unidade: 'de 0 a 100 pontos',
          current: getLatestValue(baseSummary?.overallScore),
          scenario: getLatestValue(scenarioSummary?.overallScore),
          formatar: (valor) => formatarDecimal(valor, 0)
        },
        {
          key: 'dscr',
          label: 'Cobertura do serviço da dívida',
          unidade: 'quantas vezes o resultado paga juros e amortização',
          current: getAdditionalMetric(baseAdditional, 'dscr'),
          scenario: getAdditionalMetric(scenarioAdditional, 'dscr'),
          formatar: (valor) => formatarDecimal(valor, 2)
        },
        {
          key: 'runway',
          label: 'Runway de caixa',
          unidade: 'meses que o caixa cobre',
          current: getLatestValue(baseAdditional?.runwayMeses ?? baseAdditional?.runway_meses),
          scenario: getLatestValue(scenarioAdditional?.runwayMeses ?? scenarioAdditional?.runway_meses),
          formatar: (valor) => formatarDecimal(valor, 1)
        }
      ]
    : null;

  const handleImportacaoUnificada = async ({ dre = null, balanco = null }) => {
    if (dre) {
      setDreData(dre);
      setUploadedFiles((prev) => ({ ...prev, dre: true }));
    }
    if (balanco) {
      setBalancoData(balanco);
      setUploadedFiles((prev) => ({ ...prev, balanco: true }));
    }

    const currentDre = dre || dreData;
    const currentBalanco = balanco || balancoData;

    // Gera dashboard automaticamente quando ambos estiverem carregados (melhor UX)
    if (currentDre && currentBalanco) {
      showFeedback('Gerando dashboard automaticamente...', 'info');
      await generateCompleteDashboard(currentDre, currentBalanco, {
        irParaSaude: true,
        gravarSozinho: true,
        referenciaDoBalanco: currentBalanco.dataReferencia
          ? {
              competencia: currentBalanco.dataReferencia.slice(0, 7),
              dataRef: currentBalanco.dataReferencia
            }
          : null
      });
    }
  };

  const handleViewDashboard = async () => {
    // Permite gerar dashboard mesmo com apenas uma planilha
    if (dreData || balancoData) {
      await generateCompleteDashboard(dreData, balancoData, {
        irParaSaude: true,
        gravarSozinho: true,
        referenciaDoBalanco: balancoData?.dataReferencia
          ? {
              competencia: balancoData.dataReferencia.slice(0, 7),
              dataRef: balancoData.dataReferencia
            }
          : null
      });
    }
  };

  const handleReimport = () => {
    // Volta para a tela de upload, mantendo os dados já importados
    navigateToImport();
  };

  /**
   * Gera o dashboard de um par DRE/balanço e põe o resultado na tela.
   *
   * As opções existem porque três coisas aconteciam SOZINHAS aqui, e nenhuma
   * delas podia continuar acontecendo:
   *
   * - `irParaSaude` — a função trocava a tela para Saúde sempre. Quem clicava
   *   "Gerar o parecer" dentro da Análise era arrancado justamente da tela onde
   *   o parecer sai: o produto atendia o pedido e escondia a resposta. Ligar o
   *   consentimento de IA fazia o mesmo. Agora quem navega é quem chamou — e
   *   nem ele navega se a pessoa tiver mudado de tela durante a requisição.
   * - `gravarSozinho` — só o dado que o usuário ACABOU de produzir é gravado
   *   sozinho. Devolver ao servidor o que acabou de vir dele criava uma revisão
   *   de competência a cada leitura e, com duas abas abertas, deixava a aba
   *   velha regravar por cima do mês que a outra tinha corrigido.
   * - `dreParaExibir` — janela de exibição própria, quando ela existe.
   *
   * `silencioso` é a releitura de conferência: ela atualiza os números sem
   * trocar a tela por esqueleto.
   */
  const generateCompleteDashboard = async (
    dre,
    balanco,
    {
      dreParaExibir,
      referenciaDoBalanco,
      irParaSaude = false,
      gravarSozinho = false,
      silencioso = false
    } = {}
  ) => {
    const telaAoComecar = telaAtual.current;
    if (!silencioso) setLoading(true);
    try {
      // Chamar API para gerar dashboard com dados reais
      const response = await fetch(`${API_URL}/api/upload/generate-dashboard`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          dre: dre,
          balanco: balanco,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Erro ao gerar dashboard');
      }

      // Usar dados REAIS processados pelo backend.
      // As competências do cálculo viajam junto: sem elas, uma série de
      // indicadores mais curta que o eixo exibido seria desenhada a partir da
      // primeira coluna, e a margem de junho apareceria em janeiro.
      const dados = { ...result.data };
      if (dreParaExibir) {
        dados.dre = dreParaExibir;
        dados._competenciasDoCalculo = (dre?.periodos || []).map((p) => p?.competencia).filter(Boolean);
      }
      // A data do balanço sobrevive a QUALQUER regeração. Ela só viajava junto
      // da janela de exibição, então pedir o parecer apagava da tela de que dia
      // eram os saldos — e um balanço sem data é um número sem significado.
      dados._referenciaDoBalanco = referenciaDoBalanco
        || dashboardData?._referenciaDoBalanco
        || null;

      // Gravação automática: a marca desta geração já entra resolvida quando o
      // dado NÃO é novo. Sem isto, ler a série de volta gravava a série de
      // volta — uma revisão de competência por carregamento de página.
      if (!gravarSozinho) ultimoSalvoAutomaticamente.current = dados.timestamp || null;

      setDashboardData(dados);
      // "Salvo em" só se perde quando há coisa nova para salvar.
      if (gravarSozinho) setLastSavedInfo(null);
      // Resposta atrasada não arrasta quem já foi para outra tela.
      if (irParaSaude && telaAtual.current === telaAoComecar) setActiveTab(ABAS.SAUDE);
      setScenarioData(null);
      setWhatIfAdjustments(defaultAdjustments);
    } catch (error) {
      console.error('Erro ao gerar dashboard:', error);
      showFeedback(
        `Não foi possível gerar o dashboard: ${error.message}. Os seus dados continuam aqui — tente de novo.`,
        'error'
      );
    } finally {
      if (!silencioso) setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!podeExportar) {
      showFeedback('O relatório precisa da análise gerada. Abra a Análise do Consultor e gere o parecer.', 'warning');
      return;
    }
    setExporting(true);
    try {
      const response = await fetch(`${API_URL}/api/insights/report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          consultantInsights: dashboardData.consultantInsights,
          indicadoresAdicionais: dashboardData.indicadoresAdicionais,
          balanco: dashboardData.balanco,
          metadata: { companyName: dashboardData?.dre?.empresa || 'Empresa' }
        }),
      });
      if (!response.ok) {
        throw new Error('Falha ao gerar PDF');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'consultor-virtual-relatorio.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      showFeedback(
        `${error.message || 'Não foi possível gerar o relatório'}. Tente exportar de novo em alguns instantes.`,
        'error'
      );
    } finally {
      setExporting(false);
    }
  };

  /**
   * A geração que o salvamento automático já resolveu.
   *
   * O gatilho é o `timestamp` do payload — ele muda a cada geração e não muda
   * quando a pessoa apenas navega entre telas, então cada lançamento é gravado
   * uma vez só.
   *
   * NASCE COM O TIMESTAMP DO CACHE, e é isso que fecha a corrida entre abas: a
   * aba que ABRE com dado guardado não produziu esse dado, e reenviá-lo ao
   * servidor era regravar competência com o número que aquela aba tinha de
   * antes. Duas abas abertas, a segunda recarregada depois de a primeira
   * corrigir julho, e julho voltava ao valor errado sem ninguém pedir nada.
   */
  const ultimoSalvoAutomaticamente = useRef(dashboardData?.timestamp || null);

  /**
   * Ligado pelo questionário: depois de gravar, releia a série inteira.
   *
   * O questionário mostra só a janela que acabou de ser preenchida. Quem tinha
   * dezembro guardado e lançava janeiro via o cabeçalho dizer "1 mês informado"
   * e "Leitura de jan/26" — e concluía que dezembro tinha sido derrubado. Os
   * dois meses convivem no banco; era a tela que não relia. O `handleMesFechado`
   * já faz exatamente isto, e pelo mesmo motivo.
   *
   * A releitura vem DEPOIS do salvamento, e não junto: a série é lida do
   * backend, e ler antes de gravar traria o histórico sem os meses recém-
   * enviados — o mesmo susto, com uma requisição a mais.
   */
  const relerSerieAposSalvar = useRef(false);

  useEffect(() => {
    if (authState.status !== 'authenticated') return;
    const marca = dashboardData?.timestamp || null;
    if (!marca || ultimoSalvoAutomaticamente.current === marca) return;
    ultimoSalvoAutomaticamente.current = marca;

    const gravarEReler = async () => {
      await salvarHistorico({ automatico: true });
      if (!relerSerieAposSalvar.current) return;
      relerSerieAposSalvar.current = false;
      // Silenciosa: o dashboard recém-gerado já está na tela, e a releitura
      // acrescenta os meses que já estavam no histórico.
      await carregarSerieDeCompetencias(undefined, { silencioso: true });
    };

    gravarEReler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardData?.timestamp, authState.status]);

  const handleClearData = () => setConfirmarLimpeza(true);

  const limparDadosAgora = () => {
    setConfirmarLimpeza(false);
    // Limpar todos os estados (localStorage é limpo automaticamente pelo hook)
    setDreData(null);
    setBalancoData(null);
    setDashboardData(null);
    setUploadedFiles({ dre: false, balanco: false });
    setActiveTab(ABAS.DADOS);
    setEntryMode('selection');
    setScenarioData(null);
    setScenarioLoading(false);
    setScenarioError(null);
    setWhatIfAdjustments(defaultAdjustments);
    setLastSavedInfo(null);

    showFeedback('Os dados desta sessão foram apagados.');
  };

  const handleWhatIfAdjust = (key, value) => {
    if (key === 'reset') {
      setWhatIfAdjustments(defaultAdjustments);
      setScenarioData(null);
      setScenarioError(null);
      return;
    }
    setWhatIfAdjustments((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSimulateScenario = async () => {
    // Sem balanço o painel nem chega a ser renderizado — o bloco fechado ocupa o
    // lugar dele. A guarda fica por segurança, e agora não culpa o usuário por
    // algo que ele já fez: o dashboard ESTÁ gerado.
    if (!temBalanco) {
      setScenarioError('A simulação precisa do balanço para calcular a cobertura da dívida.');
      return;
    }
    setScenarioError(null);
    setScenarioLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/insights/simulate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          dre: dashboardData.dre,
          balanco: dashboardData.balanco,
          ajustes: whatIfAdjustments
        }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Falha ao simular cenário.');
      }
      setScenarioData(result.data);
    } catch (error) {
      console.error(error);
      setScenarioData(null);
      setScenarioError(
        `${error.message || 'Não foi possível simular o cenário'}. Os seus números continuam intactos.`
      );
    } finally {
      setScenarioLoading(false);
    }
  };

  // Uma contagem só para o produto inteiro. O badge somava três listas cruas e
  // chegava a um número que nenhuma tela confirmava: ele dizia 6 enquanto a
  // Análise dizia 8 e a de Planos, "outros 4".
  const alertCount = contagemDeSinais(leitura).pedemAcao;

  const handleManualEntryFinish = async (formData) => {
    showFeedback('Processando os dados informados...', 'info');

    try {
      // Mesmo formato que a importacao de planilha produz — ver manualEntryTransform.js
      const dre = construirDRE(formData);
      const balanco = construirBalanco(formData);

      setDreData(dre);
      setBalancoData(balanco);
      setUploadedFiles({ dre: true, balanco: Boolean(balanco) });

      // Marcado ANTES de gerar: é `generateCompleteDashboard` que muda o
      // `timestamp` e dispara o salvamento automático, e é ele que precisa
      // saber que, terminada a gravação, a série tem que ser relida.
      relerSerieAposSalvar.current = authState.status === 'authenticated';

      // O backend recalcula indicadores, health score, alertas e insights
      await generateCompleteDashboard(dre, balanco, { irParaSaude: true, gravarSozinho: true });

      // O rascunho cumpriu o papel dele. A limpeza mora AQUI, e não no Wizard:
      // ele desmonta junto com a troca de tela e o `setState` dele não chega a
      // ser persistido.
      descartarRascunhoDoWizard();
      setEntryMode('selection');
      // Mesma oferta do fechamento de mês, mesmo motivo: o questionário também
      // termina na Saúde, com os números novos na tela.
      oferecerAssinatura();
    } catch (error) {
      console.error('Erro ao processar entrada manual:', error);
      showFeedback('Nao foi possivel gerar o dashboard com esses dados. Confira os valores informados.', 'error');
    }
  };

  /**
   * A entrada de dados, usada pelos dois caminhos: quem ainda nao tem dashboard
   * e quem ja tem e veio conferir o que falta. E a MESMA tela — a diferenca e
   * so que a navegacao lateral esta destravada no segundo caso.
   */
  const renderEntradaDeDados = () => {
    if (entryMode === 'manual') {
      return (
        <Wizard
          onFinish={handleManualEntryFinish}
          onCancel={() => setEntryMode('selection')}
          // O portao de meses do questionario precisa do mesmo caminho de saida
          // que o de FecharMes: quem esbarra em "6 e 12 meses exigem plano pago"
          // vai para a tela de planos levando os meses que nao couberam.
          aoVerPlanos={(bloqueio) => irParaPlanos('questionario-meses', bloqueio)}
        />
      );
    }

    if (entryMode === 'import') {
      return (
          <Box
            sx={{
              maxWidth: 1200,
              mx: 'auto',
              textAlign: 'center',
              pt: 3,
            }}
          >
            <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
              <Button 
                startIcon={<ArrowBackIcon />} 
                onClick={() => setEntryMode('selection')}
                sx={{ mr: 2 }}
              >
                Voltar
              </Button>
            </Box>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Typography
                variant="h3"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  mb: 2,
                }}
              >
                Importar planilha
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 6, maxWidth: 600, mx: 'auto' }}>
                Envie um arquivo por vez. A estrutura diz se ele contém DRE, balanço, os dois ou se precisa de conferência.
              </Typography>
            </motion.div>

            {/* Quem nao tem planilha nenhuma comeca por aqui. */}
            <BaixarModelo />

            {/* Uma porta para o nosso modelo, os formatos legados e arquivos externos. */}
            <ImportarPlanilhaLivre
              onImportado={handleImportacaoUnificada}
            />
            {/* Progress Indicator */}
            <UploadProgress
              uploadedFiles={uploadedFiles}
              onViewDashboard={handleViewDashboard}
              isProcessing={loading}
            />
          </Box>
      );
    }

    return (
      <TelaDados
        leitura={leitura}
        arquivos={uploadedFiles}
        autenticado={authState.status === 'authenticated'}
        onImportar={() => setEntryMode('import')}
        onQuestionario={() => setEntryMode('manual')}
        onFecharMes={() => setActiveTab(ABAS.FECHAR_MES)}
        // Passa por `irParaPlanos`, e não por `setActiveTab`, para a tela de
        // planos saber POR QUE alguém chegou nela — senão a coluna do Full
        // Time aparece sem o motivo que a explica.
        onConexaoDireta={() => irParaPlanos('conexao-direta')}
      />
    );
  };

  /**
   * A tela de planos, montada igual nas DUAS ramificações de render.
   *
   * Este componente existe porque `ModernDashboard` retorna de dois lugares
   * (com e sem dashboard), e uma tela ligada em só um deles é exatamente o
   * defeito que deixou `FecharMes` inalcançável por uma onda inteira.
   */
  const telaDePlanos = (
    <TelaPlanos
      dadosParaTeaser={dadosParaTeaser}
      origem={contextoDePlanos.origem}
      bloqueio={contextoDePlanos.bloqueio}
      autenticado={authState.status === 'authenticated'}
      onIrPara={handleTabChange}
      onEntrar={handleOpenLogin}
    />
  );

  /**
   * O aviso dos meses que não entraram. NÃO desaparece sozinho: ele conta uma
   * perda de dado, e mensagem de perda de dado não pode depender de a pessoa
   * estar olhando para a tela no segundo em que ela aparece.
   */
  const avisoDeMesesBloqueados = (
    <Snackbar
      open={Boolean(avisoDePeriodos)}
      // O aviso vinha sem tempo de vida e ancorado no centro: ficava eternamente
      // por cima do conteudo -- em Planos, tapava justamente a linha "Ate 3 meses
      // de historico", que e' o que ele esta explicando. Canto e tempo de vida
      // mantem o recado sem cobrir a leitura; ele nao se perde, porque o mesmo
      // limite aparece na tela de Planos e no chip de cobertura.
      autoHideDuration={15000}
      onClose={(evento, motivo) => { if (motivo !== 'clickaway') setAvisoDePeriodos(null); }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    >
      <Alert
        severity="warning"
        onClose={() => setAvisoDePeriodos(null)}
        sx={{ maxWidth: 520 }}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              irParaPlanos('mes-bloqueado', avisoDePeriodos);
              setAvisoDePeriodos(null);
            }}
          >
            Ver o que não entrou
          </Button>
        }
      >
        Nem todos os meses entraram no seu histórico.
      </Alert>
    </Snackbar>
  );

  /**
   * Apagar tudo pede confirmação na linguagem do produto.
   *
   * `window.confirm` é a caixa cinza do sistema operacional: não diz o que será
   * apagado, não combina com nada da tela e, em alguns navegadores, pode ser
   * suprimida — o que transformaria "limpar dados" numa ação sem confirmação
   * nenhuma. A ação destrutiva é a última que pode depender disso.
   */
  const dialogoDeLimpeza = (
    <Dialog open={confirmarLimpeza} onClose={() => setConfirmarLimpeza(false)}>
      <DialogTitle>Apagar os dados desta sessão?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Isso remove da tela o DRE, o balanço e o dashboard que estão carregados agora.
          {authState.status === 'authenticated'
            ? ' Os meses que você já gravou no histórico da sua conta continuam salvos.'
            : ' Como você não está com a conta conectada, não haverá como recuperar.'}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmarLimpeza(false)}>Cancelar</Button>
        <Button color="error" variant="contained" onClick={limparDadosAgora}>
          Apagar
        </Button>
      </DialogActions>
    </Dialog>
  );

  // O preenchimento guiado cola na borda no celular: 4px do casco + 0 do
  // Container (sem gutters) + 8px do Paper. A condição inclui a aba porque
  // trocar de tela não reseta o `entryMode` — sem ela, voltar à Saúde com o
  // questionário em segundo plano herdaria o respiro mínimo.
  const wizardAberto = entryMode === 'manual' && activeTab === ABAS.DADOS;
  const respiroDoWizard = wizardAberto ? { p: { xs: 0.5, sm: 3, md: 4 } } : undefined;

  // Sem dashboard gerado, a navegação de leitura fica travada e a tela é a
  // entrada de dados — mas é a mesma entrada, não uma tela paralela.
  if (!dashboardData) {
    return (
      <>
      <ModernLayout
        activeTab={activeTab}
        sxDoConteudo={respiroDoWizard}
        onTabChange={handleTabChange}
        onExport={handleExport}
        onClearData={handleClearData}
        alertCount={0}
        hasData={dreData !== null || balancoData !== null}
        exporting={exporting}
        canExport={podeExportar}
        disableDataTabs
        showImportTab
        importTabId={ABAS.DADOS}
        importTabLabel="Dados"
        showConfigTab={isAdmin}
        showFecharMesTab={authState.status === 'authenticated'}
        authUser={authState.user}
        onRequestLogin={handleOpenLogin}
        onLogout={handleLogout}
        lastSavedAt={currentLastSavedAt}
      >
        {activeTab === ABAS.FECHAR_MES && (
          <FecharMes
          aoConcluir={handleMesFechado}
          aoVerPlanos={(bloqueio) => irParaPlanos('mes-bloqueado', bloqueio)}
        />
        )}
        {activeTab === ABAS.PLANOS && telaDePlanos}
        {activeTab !== ABAS.FECHAR_MES && activeTab !== ABAS.PLANOS && renderEntradaDeDados()}
      </ModernLayout>
      <AuthDialog open={authDialogOpen} onClose={handleCloseLogin} onSuccess={handleLoginSuccess} />
      <Snackbar
        open={feedback.open}
        autoHideDuration={4000}
        onClose={handleFeedbackClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleFeedbackClose} severity={feedback.severity} sx={{ width: '100%' }}>
          {feedback.message}
        </Alert>
      </Snackbar>
      {avisoDeMesesBloqueados}
      {dialogoDeLimpeza}
      </>
    );
  }

  // Main Dashboard Content
  return (
    <>
    <ModernLayout
      activeTab={activeTab}
      sxDoConteudo={respiroDoWizard}
      onTabChange={handleTabChange}
      onExport={handleExport}
      onClearData={handleClearData}
      alertCount={alertCount}
      onReimport={handleReimport}
      hasData={true}
      exporting={exporting}
      canExport={podeExportar}
      disableDataTabs={!dashboardData}
      showImportTab
      importTabId={ABAS.DADOS}
      importTabLabel="Dados"
      showConfigTab={isAdmin}
      showFecharMesTab={authState.status === 'authenticated'}
      authUser={authState.user}
      onRequestLogin={handleOpenLogin}
      onLogout={handleLogout}
      onSave={() => salvarHistorico()}
      canSave={Boolean(dashboardData)}
      saving={saveLoading}
      lastSavedAt={currentLastSavedAt}
    >
      {/* Dados — visitar nao apaga nada */}
      {activeTab === ABAS.DADOS && renderEntradaDeDados()}

      {/* UM GUIA SÓ NO TOPO, E ELE MUDA CONFORME O QUE A PESSOA DEVE.
          Quem volta e tem iniciativa em aberto recebe "o que mudou desde a
          última visita e o que fazer hoje"; quem não tem recebe o guia que
          ensina o próximo passo do dado.

          Não são dois cartões empilhados de propósito: a 390px, dois guias
          somam mais de 250px e empurram o Índice de Saúde — a primeira dobra
          declarada do produto — para baixo do corte. Este código já cometeu
          esse defeito uma vez, com um guia só, e o corrigiu encolhendo o
          cartão (ver ProximoPasso.js). O que falta do lado do dado continua
          dito dentro das telas, pelos blocos bloqueados. */}
      {TELAS_DE_LEITURA.includes(activeTab) && !loading && (
        <Box sx={{ maxWidth: LARGURA_DE_LEITURA, mx: 'auto', mb: 3 }}>
          {temPrimeiraDobraDeRetorno ? (
            <DesdeAUltimaVisita
              retorno={retornoDaVisita}
              onVerPlano={activeTab === ABAS.ANALISE ? null : () => handleTabChange(ABAS.ANALISE)}
            />
          ) : (
            <ProximoPasso
              leitura={leitura}
              onIrPara={handleTabChange}
              onEntrar={handleOpenLogin}
              autenticado={authState.status === 'authenticated'}
            />
          )}
        </Box>
      )}

      {/* A troca de tela ganha 180ms de continuidade: o conteúdo entra de onde
          o anterior saiu, em vez de piscar. Curto o bastante para não atrasar
          quem está em tarefa — e a `key` garante que cada tela entre uma vez,
          não toda vez que um número muda. */}
      {/* O esqueleto SUBSTITUI a tela; ele não se soma a ela. Faltava a guarda
          `!loading` em Dívida e Retorno e no Painel, e o carregamento aparecia
          com a tela inteira desenhada logo abaixo.

          A Análise fica de fora de propósito: ali o que carrega é UM bloco — o
          parecer —, e os sinais e o plano 30-60-90 que já estão na tela não
          mudam. Trocar a tela lida por barras cinzas seria perda de conteúdo; o
          botão é que diz que está trabalhando. */}
      {loading && TELAS_DE_LEITURA.includes(activeTab) && activeTab !== ABAS.ANALISE && (
        <EsqueletoDaTela />
      )}

      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
      >

      {/* Saúde — a primeira dobra do produto */}
      {!loading && activeTab === ABAS.SAUDE && (
        <Stack spacing={3} sx={{ maxWidth: LARGURA_DE_LEITURA, mx: 'auto' }}>
          <TelaSaude leitura={leitura} onIrPara={handleTabChange} />
          {/* A oferta fica DEPOIS do resultado. Quem acabou de lançar veio ver o
              número: pôr a oferta acima empurraria o Índice de Saúde para baixo
              da dobra no exato momento em que ele é a resposta. */}
          <OfertaAoConcluir
            oferta={ofertaDeAssinatura}
            aoVerPlanos={() => irParaPlanos('lancamento-concluido')}
            aoDispensar={() => setOfertaDeAssinatura(null)}
          />
        </Stack>
      )}

      {/* Resultado — os dois resultados, com o CMV explicito entre eles */}
      {!loading && activeTab === ABAS.RESULTADO && <TelaResultado leitura={leitura} />}

      {/* Caixa e Ciclo — os dois blocos que fecham inteiros sem balanco */}
      {!loading && activeTab === ABAS.CAIXA_CICLO && <TelaCaixaCiclo leitura={leitura} onIrPara={handleTabChange} />}

      {/* Divida e Retorno — fronteira por dependencia de dado, nao por tema.
          A guarda `!loading` é a mesma das telas vizinhas: sem ela o esqueleto
          NÃO substituía a tela, ele se somava a ela — a análise inteira ficava
          desenhada abaixo do carregamento, com os botões vivos. */}
      {!loading && activeTab === ABAS.DIVIDA_RETORNO && (
        <TelaDividaRetorno leitura={leitura} onIrPara={handleTabChange} />
      )}

      {/* Painel de Indicadores — a tela densa, para quem trabalha o dado.
          Absorve a aba Indicadores e o que restava da Visao Geral. */}
      {!loading && activeTab === ABAS.PAINEL && (
        <Stack spacing={3} sx={{ maxWidth: LARGURA_DE_LEITURA, mx: 'auto' }}>
          <TelaPainel leitura={leitura} onIrPara={handleTabChange} />
          {temBalanco ? (
            <WhatIfPanel
              adjustments={whatIfAdjustments}
              onAdjust={handleWhatIfAdjust}
              onSimulate={handleSimulateScenario}
              loading={scenarioLoading}
              scenario={scenarioData}
              highlights={scenarioHighlights}
              erro={scenarioError}
              aoDispensarErro={() => setScenarioError(null)}
              competenciaProjetada={competenciaProjetada}
            />
          ) : (
            <BlocoBloqueado
              titulo="Simulador de cenário"
              pergunta="O que acontece com o meu resultado se eu vender mais ou gastar menos?"
              chamada={estadoDoSimulador.chamada}
              campos={estadoDoSimulador.campos}
              bloqueados={estadoDoSimulador.bloqueados}
              onDestravar={() => handleTabChange(ABAS.DADOS)}
            />
          )}
        </Stack>
      )}

      {/* DRE Tab */}
      {activeTab === ABAS.DRE && <DRETab dreData={dashboardData.dre} />}

      {/* Balanço Tab */}
      {activeTab === ABAS.BALANCO && (
        <TelaBalanco
          leitura={leitura}
          balanco={dashboardData.balanco || balancoData}
          onIrPara={handleTabChange}
        />
      )}

      {/* Analise do Consultor — o deterministico sempre, e o motivo quando o
          parecer por IA nao sai */}
      {activeTab === ABAS.ANALISE && (
        <TelaAnalise
          leitura={leitura}
          onIrPara={handleTabChange}
          aoAtualizarConsentimento={carregarSerieDeCompetencias}
          aoVerPlanos={() => irParaPlanos('parecer')}
          // Rota própria, e sem sair daqui: o parecer nasce e é GRAVADO com a
          // impressão digital do dado que o produziu. É o mesmo botão que
          // atualiza um parecer que envelheceu.
          aoPedirParecer={pedirParecer}
          // Marcar e adiar NÃO acionam modelo e NÃO dependem de assinatura: o
          // plano de ação é determinístico e é do tier gratuito.
          aoMarcarTarefa={marcarIniciativa}
          salvandoTarefa={salvandoTarefa}
          // O botão mais caro do produto: cada clique é uma chamada de LLM
          // cobrada. Sem estado de trabalho ele aceitava clique repetido — três
          // cliques em 351 ms geravam TRÊS gerações de dashboard.
          gerandoParecer={gerandoParecer}
        />
      )}

      </motion.div>

      {/* Fechar o mês */}
      {activeTab === ABAS.FECHAR_MES && (
        <FecharMes
          aoConcluir={handleMesFechado}
          aoVerPlanos={(bloqueio) => irParaPlanos('mes-bloqueado', bloqueio)}
        />
      )}

      {/* Planos — o paywall, com o sinal mais crítico da própria empresa */}
      {activeTab === ABAS.PLANOS && telaDePlanos}

      {/* Configurações Tab */}
      {activeTab === ABAS.CONFIG && (
        isAdmin ? (
          <Box>
            <Box sx={{ mb: 3 }}>
              <AlertConfigTab />
            </Box>
            <InsightsConfigEditor />
          </Box>
        ) : (
          <Box sx={{ p: 4 }}>
            <Alert severity="warning">Aba disponível somente para administradores.</Alert>
          </Box>
        )
      )}
    </ModernLayout>
      <AuthDialog open={authDialogOpen} onClose={handleCloseLogin} onSuccess={handleLoginSuccess} />
    <Snackbar
      open={feedback.open}
      autoHideDuration={4000}
      onClose={handleFeedbackClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert onClose={handleFeedbackClose} severity={feedback.severity} sx={{ width: '100%' }}>
        {feedback.message}
      </Alert>
    </Snackbar>
    {avisoDeMesesBloqueados}
    {dialogoDeLimpeza}
    </>
  );
};

export default ModernDashboard;
